/**
 * Opciones de los catálogos respaldados por un `origen` (aliados/ubicaciones).
 *
 * La definición no enumera esas opciones: el Admin Web las carga desde el mismo
 * listado del backoffice que usa el resto de la pantalla. La caché es por origen
 * y por sesión de módulo, así que cambiar de reporte no repite la llamada.
 */
import { useEffect, useState } from 'react';
import type { ReportCatalogOrigin, ReportFilterOption } from '@goelectric/shared-types';
import { allyApi } from '@/features/aliados/api/allyApi';
import { allyDisplayName } from '@/features/aliados/utils/format';
import { locationApi } from '@/features/locaciones/api/locationApi';

export type OrigenCatalogo = ReportCatalogOrigin;

export type EstadoOrigen = 'cargando' | 'listo' | 'error';

export interface CatalogosOrigenState {
  opciones: Partial<Record<OrigenCatalogo, ReportFilterOption[]>>;
  estado: Partial<Record<OrigenCatalogo, EstadoOrigen>>;
}

/** Mismo tope que los selectores de aliado (el listado del contrato es paginado). */
const LIMITE_OPCIONES = 100;

const cache = new Map<OrigenCatalogo, ReportFilterOption[]>();
const enCurso = new Map<OrigenCatalogo, Promise<ReportFilterOption[]>>();

async function cargarOrigen(origen: OrigenCatalogo): Promise<ReportFilterOption[]> {
  const cacheado = cache.get(origen);
  if (cacheado) return cacheado;

  const pendiente = enCurso.get(origen);
  if (pendiente) return pendiente;

  const promesa = (
    origen === 'aliados'
      ? allyApi
          .list({ limit: LIMITE_OPCIONES })
          .then((page) =>
            page.data.map((aliado) => ({ valor: aliado.id, etiqueta: allyDisplayName(aliado) })),
          )
      : locationApi
          .list({ limit: LIMITE_OPCIONES })
          .then((page) =>
            page.data.map((locacion) => ({ valor: locacion.id, etiqueta: locacion.name })),
          )
  )
    .then((opciones) => {
      cache.set(origen, opciones);
      return opciones;
    })
    .finally(() => {
      enCurso.delete(origen);
    });

  enCurso.set(origen, promesa);
  return promesa;
}

/** Limpia la caché en memoria (solo tests). */
export function resetCatalogoOrigenesCache(): void {
  cache.clear();
  enCurso.clear();
}

function estadoInicial(origenes: OrigenCatalogo[]): CatalogosOrigenState {
  const opciones: CatalogosOrigenState['opciones'] = {};
  const estado: CatalogosOrigenState['estado'] = {};
  for (const origen of origenes) {
    const cacheado = cache.get(origen);
    if (cacheado) {
      opciones[origen] = cacheado;
      estado[origen] = 'listo';
    } else if (enCurso.has(origen)) {
      estado[origen] = 'cargando';
    }
  }
  return { opciones, estado };
}

export function useCatalogoOrigenes(origenes: OrigenCatalogo[]): CatalogosOrigenState {
  const clave = origenes.join('|');
  const [state, setState] = useState<CatalogosOrigenState>(() => estadoInicial(origenes));

  useEffect(() => {
    const unicos = [...new Set(clave.split('|').filter(Boolean))] as OrigenCatalogo[];
    const inicial = estadoInicial(unicos);
    setState(inicial);

    let cancelled = false;
    for (const origen of unicos) {
      if (inicial.estado[origen] === 'listo') continue;
      setState((prev) => ({ ...prev, estado: { ...prev.estado, [origen]: 'cargando' } }));
      cargarOrigen(origen)
        .then((opciones) => {
          if (cancelled) return;
          setState((prev) => ({
            opciones: { ...prev.opciones, [origen]: opciones },
            estado: { ...prev.estado, [origen]: 'listo' },
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setState((prev) => ({ ...prev, estado: { ...prev.estado, [origen]: 'error' } }));
        });
    }

    return () => {
      cancelled = true;
    };
  }, [clave]);

  return state;
}
